import React from 'react';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { ExportButton, InfoTip } from './shared';
import { VALID_PERIODS, formatDateTime, periodLabel, relativeTime } from '../utils';

const COMPACT_PERIOD_LABELS = { '7d': '7D', '30d': '30D', '90d': '90D', '365d': '1Y' };

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.spacing(2), flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: { gap: theme.spacing(1.5) },
    [theme.breakpoints.between('sm', 'md')]: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(1.5),
    },
  },
  intro: { minWidth: 0, flex: '1 1 360px' },
  eyebrow: { color: theme.palette.primary.main, fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em' },
  titleRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), minWidth: 0 },
  title: { color: 'var(--dm-text-strong, #263548)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 750, letterSpacing: '-.025em', lineHeight: 1.2, marginTop: 4, [theme.breakpoints.down('xs')]: { fontSize: '1.5rem' } },
  titleInfo: { flex: '0 0 auto', marginTop: 4, color: 'var(--dm-muted, #647184)' },
  scope: { color: 'var(--dm-muted, #647184)', fontSize: '0.8125rem', lineHeight: 1.5, marginTop: 6, maxWidth: 720 },
  controls: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', width: '100%' },
    [theme.breakpoints.between('sm', 'md')]: { display: 'contents' },
  },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: theme.spacing(1), flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: { width: '100%', justifyContent: 'flex-end', '& .MuiButton-root': { minHeight: 36 } },
    [theme.breakpoints.between('sm', 'md')]: { gridColumn: '1 / -1', justifySelf: 'end' },
  },
  periods: {
    padding: 2,
    border: '1px solid var(--dm-border, #e7ebf0)',
    borderRadius: 10,
    background: 'var(--dm-row-alt, #f8fafc)',
    '& .MuiButtonGroup-grouped': {
      minWidth: 48,
      padding: '5px 10px',
      border: '0 !important',
      borderRadius: '8px !important',
      color: 'var(--dm-muted, #647184)',
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    '& .MuiButton-containedPrimary': {
      color: '#fff',
      boxShadow: '0 2px 6px rgba(61, 125, 221, .24)',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      '& .MuiButtonGroup-grouped': { flex: 1, minWidth: 0 },
    },
    [theme.breakpoints.between('sm', 'md')]: { alignSelf: 'end' },
  },
  spin: { animation: '$spin .8s linear infinite' },
  '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
}));

const InsightsHeader = ({ title = 'Platform command centre', eyebrow = 'Overview', meta = {}, period, onPeriodChange, onRefresh, refreshing, exportKey = 'all', exportLabel = 'Export page', scopeInTooltip = false, children }) => {
  const classes = useStyles();
  const periods = Array.isArray(meta.available_periods) && meta.available_periods.length ? meta.available_periods : VALID_PERIODS;
  const excluded = Array.isArray(meta.excluded_companies) ? meta.excluded_companies.filter(Boolean) : [];
  const scopeText = [
    meta.scope,
    excluded.length ? `Testing accounts hidden: ${excluded.join(', ')}` : '',
  ].filter(Boolean).join(' ');
  return (
    <header className={classes.header}>
      <div className={classes.intro}>
        {eyebrow ? <Typography className={classes.eyebrow}>{eyebrow}</Typography> : null}
        <div className={classes.titleRow} data-testid="insights-title-row">
          <Typography component="h1" className={classes.title}>{title}</Typography>
          {scopeText && scopeInTooltip ? (
            <span className={classes.titleInfo}>
              <InfoTip title={scopeText} label="Insights scope">
                <InfoOutlinedIcon style={{ fontSize: 18 }} />
              </InfoTip>
            </span>
          ) : null}
        </div>
        {children}
        {scopeText && !scopeInTooltip ? (
            <Typography className={classes.scope} data-testid="insights-scope">
              {meta.scope}
              {excluded.length ? (
                <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                  <InfoTip title={`Testing accounts hidden: ${excluded.join(', ')}`} label="Testing accounts hidden">
                    <InfoOutlinedIcon style={{ fontSize: 16 }} />
                  </InfoTip>
                </span>
              ) : null}
            </Typography>
        ) : null}
      </div>
      <div className={classes.controls}>
        {onPeriodChange ? (
          <ButtonGroup size="small" className={classes.periods} aria-label="Dashboard period">
            {periods.map(key => (
              <Tooltip key={key} title={periodLabel(key)} arrow>
                <Button
                  color={period === key ? 'primary' : 'default'}
                  variant={period === key ? 'contained' : 'text'}
                  onClick={() => onPeriodChange(key)}
                  aria-label={periodLabel(key)}
                  aria-pressed={period === key}
                >{COMPACT_PERIOD_LABELS[key] || periodLabel(key)}</Button>
              </Tooltip>
            ))}
          </ButtonGroup>
        ) : null}
        <div className={classes.row}>
          {meta.generated_at ? (
            <Tooltip title={relativeTime(meta.generated_at)} arrow>
              <Typography variant="caption" color="textSecondary" data-testid="insights-updated">
                Updated {formatDateTime(meta.generated_at)}
              </Typography>
            </Tooltip>
          ) : null}
          {onRefresh ? (
            <Button
              size="small"
              variant="outlined"
              onClick={onRefresh}
              disabled={refreshing}
              startIcon={<RefreshIcon className={refreshing ? classes.spin : undefined} fontSize="small" />}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          ) : null}
          <ExportButton exportKey={exportKey} label={exportLabel} />
        </div>
      </div>
    </header>
  );
};

export default InsightsHeader;
